import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import {
    GoogleMap,
    Marker,
    Autocomplete,
    useJsApiLoader,
    Circle
} from "@react-google-maps/api";
import { Input, message, Button } from "antd";
import { EnvironmentOutlined, AimOutlined } from "@ant-design/icons";
import GlobalButton from "./GlobalButton";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyCOBxdTr-iPKepu-nUpPRHL9jTte7UTcNQ";

const libraries = ["places"];

const containerStyle = {
    width: "100%",
    height: "100%"
};

const DEFAULT_POS = { lat: 11.9670699, lng: 79.0924025 };

/**
 * GlobalMap Component
 * High-performance, production-ready Google Maps integration.
 * Supports:
 * - Places Autocomplete Search
 * - Detect Current Location
 * - Reverse Geocoding (Map Click)
 * - Draggable Marker (Pin Sync)
 * - Radius Visualization
 * - Parent State Synchronization
 */
const GlobalMap = ({
    initialPosition = DEFAULT_POS,
    onLocationSelect,
    height = "300px",
    showSearch = true,
    showDetect = true,
    markerPosition = null,
    address = "",
    radius = 0,
    draggable = true,
    zoom = 15,
    onReset = null,
    isOpen = false
}) => {
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    // Internal states synced with props
    const [position, setPosition] = useState(markerPosition || initialPosition);
    const [searchValue, setSearchValue] = useState(address);
    const [mapCenter, setMapCenter] = useState(markerPosition || initialPosition);
    const [detecting, setDetecting] = useState(false);

    // Refs for synchronization to avoid re-render loops
    const lastProcessedPos = useRef(markerPosition);
    const lastProcessedAddress = useRef(address);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_KEY,
        libraries
    });

    /* -------- Robust Event Shield (Capture Phase) -------- */
    // Prevents issues with Ant Design Popovers closing when interacting with Google's autocomplete dropdown
    useEffect(() => {
        const handleEventShield = (e) => {
            const target = e.target;
            if (target && target.closest && (target.closest('.pac-container') || target.closest('.pac-item') || target.closest('.global-map-wrapper'))) {
                if (target.closest('.pac-container') || target.closest('.pac-item')) {
                    e.stopImmediatePropagation();
                    e.stopPropagation();
                }
            }
        };

        const eventTypes = ['mousedown', 'click', 'pointerdown', 'touchstart'];
        eventTypes.forEach(type => {
            window.addEventListener(type, handleEventShield, true);
            document.addEventListener(type, handleEventShield, true);
        });

        const styleId = "pac-container-fix-styles";
        if (!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.innerHTML = `
                .pac-container { 
                    z-index: 99999 !important; 
                    pointer-events: auto !important;
                }
            `;
            document.head.appendChild(style);
        }

        return () => {
            eventTypes.forEach(type => {
                window.removeEventListener(type, handleEventShield, true);
                document.removeEventListener(type, handleEventShield, true);
            });
            window.__isMapSearching = false;
        };
    }, []);

    /* -------- Sync External Props -------- */
    useEffect(() => {
        if (markerPosition && typeof markerPosition.lat === 'number' && typeof markerPosition.lng === 'number') {
            const isDifferent = !lastProcessedPos.current ||
                lastProcessedPos.current.lat !== markerPosition.lat ||
                lastProcessedPos.current.lng !== markerPosition.lng;
            if (isDifferent) {
                const newPos = { lat: markerPosition.lat, lng: markerPosition.lng };
                setPosition(newPos);
                setMapCenter(newPos);
                lastProcessedPos.current = newPos;
            }
        } else if (!markerPosition && lastProcessedPos.current) {
            setPosition(initialPosition);
            setMapCenter(initialPosition);
            lastProcessedPos.current = null;
        }

        if (address !== undefined && address !== lastProcessedAddress.current) {
            setSearchValue(address || "");
            lastProcessedAddress.current = address || "";
        }
    }, [markerPosition, address, initialPosition]);

    /* -------- Re-sync when Popover Reopens -------- */
    useEffect(() => {
        if (isOpen) {
            setSearchValue(address || "");
            lastProcessedAddress.current = address || "";
        }
    }, [isOpen, address]);



    /* -------- Location Update Core -------- */
    const handleUpdate = useCallback((lat, lng, formattedAddress, rawData) => {
        const newPos = { lat, lng };
        setPosition(newPos);
        setMapCenter(newPos);
        setSearchValue(formattedAddress);

        lastProcessedPos.current = newPos;
        lastProcessedAddress.current = formattedAddress;

        if (mapRef.current) {
            mapRef.current.panTo(newPos);
        }

        const components = rawData?.address_components || [];
        const getComp = (type) => components.find((c) => c.types.includes(type))?.long_name || "";

        let zipCode = getComp("postal_code") || getComp("zip_code");
        // Fallback 1: Search all components for anything that looks like a pincode
        if (!zipCode) {
            for (const comp of components) {
                if (/\b\d{6}\b/.test(comp.long_name)) {
                    zipCode = comp.long_name.match(/\b\d{6}\b/)[0];
                    break;
                }
            }
        }
        // Fallback 2: Regex in formatted address
        if (!zipCode && formattedAddress) {
            const match = formattedAddress.match(/\b\d{6}\b/);
            if (match) zipCode = match[0];
        }

        let district = getComp("administrative_area_level_2");
        // Fallback: search for "District" in any component
        if (!district) {
            const distComp = components.find(c =>
                c.long_name.toLowerCase().includes("district") ||
                c.types.includes("administrative_area_level_3")
            );
            district = distComp ? distComp.long_name.replace(/ district/i, "") : "";
        }
        // Final fallback: Use locality if district is empty (common for city-districts)
        if (!district) district = getComp("locality");

        // City extraction fallback hierarchy
        let city = getComp("neighborhood") ||
            getComp("sublocality_level_1") ||
            getComp("sublocality") ||
            getComp("locality") ||
            getComp("administrative_area_level_3") ||
            district ||
            "Unknown City";

        if (rawData?.name && !rawData.name.includes("+")) {
            city = rawData.name;
        } else if (formattedAddress) {
            const parts = formattedAddress.split(',').map(p => p.trim());
            // If the address starts with a Plus Code, the second part is usually the local village/area
            if (parts[0].includes('+') && parts.length > 1) {
                city = parts[1];
            } else if (!parts[0].includes('+') && (city === "Unknown City" || city === district)) {
                city = parts[0];
            }
        }

        onLocationSelect?.({
            latitude: lat,
            longitude: lng,
            address: formattedAddress,
            city: city,
            district: district,
            state: getComp("administrative_area_level_1"),
            zipCode: zipCode,
            country: getComp("country"),
            raw: rawData
        });
    }, [onLocationSelect]);

    // Fix: Auto-fill address if position exists but address is empty (e.g. from GPS context)
    useEffect(() => {
        if (isLoaded && markerPosition && (!address || address.trim() === "") && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: markerPosition }, (results, status) => {
                if (status === "OK" && results[0]) {
                    // We only want to notify parent, not necessarily move the map (position is already correct)
                    // But handleUpdate does both. It's safe since position matches.
                    handleUpdate(markerPosition.lat, markerPosition.lng, results[0].formatted_address, results[0]);
                }
            });
        }
    }, [isLoaded, markerPosition, address, handleUpdate]);

    /* -------- Event Handlers -------- */

    // 1. Google Places Autocomplete selection
    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (place?.geometry) {
            const loc = place.geometry.location;
            const lat = loc.lat();
            const lng = loc.lng();

            // If place is broad or missing details, reverse geocode to get full components
            const isBroad = place.types?.some(t => ['locality', 'administrative_area_level_1', 'administrative_area_level_2'].includes(t));
            const hasMissingDetails = !place.address_components?.some(c => c.types.includes('postal_code')) || !place.address_components?.some(c => c.types.includes('administrative_area_level_2'));

            if ((isBroad || hasMissingDetails) && window.google) {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        results[0].name = place.name;
                        handleUpdate(lat, lng, results[0].formatted_address, results[0]);
                    } else {
                        handleUpdate(lat, lng, place.formatted_address || place.name, place);
                    }
                });
            } else {
                handleUpdate(lat, lng, place.formatted_address || place.name, place);
            }
        }
    };

    // 2. Reverse Geocoding (Map Click or Pin Drag)
    const reverseGeocode = useCallback((lat, lng) => {
        if (!window.google) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                const country = results[0].address_components.find(c => c.types.includes("country"))?.short_name;
                if (country && country !== "IN") {
                    console.warn(`🌍 Location detected outside India (${country}). If this is unexpected, check your VPN or browser settings.`);
                    message.warning({
                        content: `Detected location is in ${results[0].formatted_address}. If you are in India, please check if your VPN is on.`,
                        duration: 5,
                        key: "countryWarning"
                    });
                }
                handleUpdate(lat, lng, results[0].formatted_address, results[0]);
            }
        });
    }, [handleUpdate]);

    // 3. User manually drags the pin
    const onMarkerDragEnd = (e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        reverseGeocode(newLat, newLng);
    };

    // 4. Detect browser location with enhanced error handling and UX
    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            message.error("Geolocation is not supported by your browser");
            return;
        }

        setDetecting(true);
        const loadingKey = "detectLocation";
        message.loading({ content: "Detecting your location...", key: loadingKey, duration: 0 });

        const performDetection = (highAccuracy = true) => {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const lat = pos.coords.latitude;
                    const lng = pos.coords.longitude;

                    console.log(`🎯 raw_coordinates_detected: ${lat}, ${lng} (accuracy: ${pos.coords.accuracy}m, mode: ${highAccuracy ? 'GPS' : 'Network'})`);

                    const newPos = { lat, lng };
                    setPosition(newPos);
                    setMapCenter(newPos);
                    if (mapRef.current) {
                        mapRef.current.panTo(newPos);
                    }

                    reverseGeocode(lat, lng);
                    setDetecting(false);
                    message.success({ content: "Location detected successfully!", key: loadingKey, duration: 2 });
                },
                (err) => {
                    // Fallback strategy: if high accuracy fails, try normal accuracy
                    if (highAccuracy && (err.code === err.TIMEOUT || err.code === err.POSITION_UNAVAILABLE)) {
                        console.warn("⚠️ High accuracy detection failed, retrying with normal accuracy...");
                        performDetection(false);
                        return;
                    }

                    setDetecting(false);
                    let errorMsg = "❌ Unable to detect location. ";
                    switch (err.code) {
                        case err.PERMISSION_DENIED:
                            errorMsg += "Please allow location access in your browser settings.";
                            break;
                        case err.POSITION_UNAVAILABLE:
                            errorMsg += "Location information is unavailable. Please check your network/GPS.";
                            break;
                        case err.TIMEOUT:
                            errorMsg += "Location request timed out. Please try again or move to a clearer area.";
                            break;
                        default:
                            errorMsg += "An unknown error occurred.";
                    }
                    message.error({ content: errorMsg, key: loadingKey, duration: 4 });
                },
                {
                    enableHighAccuracy: highAccuracy,
                    timeout: highAccuracy ? 8000 : 15000,
                    maximumAge: 0
                }
            );
        };

        performDetection(true);
    };

    // 5. Hard Geocoding (Enter key in search)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            setTimeout(() => {
                if (window.google && searchValue.trim() && lastProcessedAddress.current !== searchValue) {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ address: searchValue }, (results, status) => {
                        if (status === "OK" && results[0]) {
                            const loc = results[0].geometry.location;
                            handleUpdate(loc.lat(), loc.lng(), results[0].formatted_address, results[0]);
                        }
                    });
                }
            }, 200);
        }
    };

    // 6. Reset search input value only
    const handleLocalReset = () => {
        setSearchValue("");
    };

    if (loadError) return <div className="pcu-padding-20 pcu-text-error">Map Error: {loadError.message}</div>;
    if (!isLoaded) return <div className="pcu-padding-20">Initializing Geolocation...</div>;

    const circleOptions = {
        fillColor: "#1677ff",
        fillOpacity: 0.1,
        strokeColor: "#1677ff", strokeOpacity: 0.3, strokeWeight: 1, clickable: false
    };
    return (
        <div className="global-map-wrapper">
            {(showSearch || showDetect || onReset) && (
                <div className="global-map-header">
                    {showSearch && (
                        <div className="global-map-search">
                            <Autocomplete
                                onLoad={(ref) => { autocompleteRef.current = ref; }}
                                onPlaceChanged={onPlaceChanged}
                                options={{
                                    componentRestrictions: { country: "in" },
                                    fields: ["address_components", "geometry", "formatted_address", "name"]
                                }}
                            >
                                <Input
                                    placeholder="Search location..."
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => { window.__isMapSearching = true; }}
                                    onBlur={() => { setTimeout(() => { window.__isMapSearching = false; }, 300); }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    suffix={<EnvironmentOutlined />}
                                    allowClear
                                    className="global-map-input"
                                />
                            </Autocomplete>
                        </div>
                    )}
                    {showDetect && (
                        <GlobalButton
                            icon={<AimOutlined spin={detecting} />}
                            onClick={handleDetectLocation}
                            type="primary"
                            loading={detecting}
                            disabled={detecting}
                            className="global-map-detect-btn"
                        >
                            {detecting ? "Detecting..." : "Detect"}
                        </GlobalButton>
                    )}
                    {onReset && (
                        <Button
                            onClick={handleLocalReset}
                            className="global-map-reset-btn"
                        >
                            Clear
                        </Button>
                    )}
                </div>
            )}

            <div className="pcu-radius-10 pcu-overflow-hidden pcu-border pcu-relative" style={{ height, position: 'relative' }}>
                {/* Center Control Floating Action */}
                <div
                    onClick={handleDetectLocation}
                    title="Center on current location"
                    style={{
                        position: 'absolute',
                        right: '12px',
                        bottom: '24px',
                        zIndex: 10,
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        cursor: 'pointer'
                    }}
                >
                    <AimOutlined style={{ color: '#1677ff', fontSize: '16px' }} />
                </div>

                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={mapCenter}
                    zoom={zoom}
                    onClick={(e) => reverseGeocode(e.latLng.lat(), e.latLng.lng())}
                    onLoad={(map) => (mapRef.current = map)}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        zoomControl: true,
                        gestureHandling: "greedy"
                    }}
                >
                    {position && (
                        <Marker
                            position={position}
                            draggable={draggable}
                            onDragEnd={onMarkerDragEnd}
                            animation={window.google.maps.Animation.DROP}
                        />
                    )}
                    {radius > 0 && position && (
                        <Circle
                            center={position}
                            radius={radius * 1000}
                            options={circleOptions}
                        />
                    )}
                </GoogleMap>
            </div>
            <div className="pcu-margin-top-4 pcu-text-11 pcu-text-light">
                * You can drag the pin or click on the map to refine the location.
            </div>
        </div>
    );
};

export default memo(GlobalMap);
